import type { ContentFilters, ContentRecord, ContentSection, ContentSort, ContentTopic, SourceId } from "../types/content.ts";

export const SOURCE_LABELS: Record<SourceId, string> = {
  vpsknow: "VPSKnow",
  kejilion: "科技 Lion",
  maobuni: "猫不腻",
};

export const SOURCE_SHORT_LABELS: Record<SourceId, string> = {
  vpsknow: "VK",
  kejilion: "KL",
  maobuni: "MB",
};

export function normalizeSearch(value: string): string {
  return value.normalize("NFKC").toLocaleLowerCase("zh-CN").replace(/\s+/g, " ").trim();
}

export function getRecordYear(record: ContentRecord): string {
  return record.published_at?.slice(0, 4) ?? "undated";
}

function taxonomyText(record: ContentRecord): string {
  return normalizeSearch([
    record.title,
    record.summary,
    ...record.categories,
    ...record.tags,
    ...record.key_facts,
  ].join(" "));
}

export function isVpsReview(record: ContentRecord): boolean {
  return record.categories.some((category) => normalizeSearch(category).replace(/\s/g, "") === "vps测评");
}

export function isAirportContent(record: ContentRecord): boolean {
  return normalizeSearch(record.title).includes("机场");
}

const TOOL_CATEGORIES = new Set(["站长工具", "网络工具", "AI工具"]);
const RESOURCE_CATEGORIES = new Set([
  "云服务器-海外", "云服务器-国内", "云服务器-社区", "社交娱乐", "科技lion",
  "域名服务商", "友情链接", "推广活动", "跨境支付", "网盘云盘", "软件下载",
  "邮箱直链", "eSim",
  "社区与站点",
]);

function isResourceContent(record: ContentRecord): boolean {
  if (record.categories.some((category) => RESOURCE_CATEGORIES.has(category))) return true;
  if (record.content_type === "directory_entry") {
    return !record.categories.some((category) => TOOL_CATEGORIES.has(category));
  }
  if (record.content_type === "navigation") return true;
  if (record.content_type !== "page") return false;
  return /隐私|关于|联系|服务条款|联盟|广告|投稿|友情链接|站点地图|标签/.test(record.title);
}

export function getRecordSection(record: ContentRecord): ContentSection {
  if (isVpsReview(record)) return "reviews";
  if (isResourceContent(record)) return "resources";
  if (record.content_type === "directory_entry") return "tools";
  return "knowledge";
}

const KNOWLEDGE_LABELS: Array<{ label: string; terms: string[] }> = [
  { label: "Linux 与系统", terms: ["linux", "脚本", "系统", "docker", "shell", "命令", "运维", "安全"] },
  { label: "AI 与开发", terms: ["ai", "chatgpt", "openai", "claude", "开发", "编程", "代码", "api"] },
  { label: "部署与容器", terms: ["建站", "部署", "网站", "nginx", "wordpress", "面板", "域名", "docker", "compose"] },
  { label: "故障排查", terms: ["故障", "排障", "排查", "恢复", "回滚", "错误", "失败"] },
  { label: "网络与安全", terms: ["网络", "代理", "ip", "bgp", "线路", "带宽", "dns", "clash", "sing-box"] },
  { label: "VPS 指南", terms: ["vps", "云主机", "服务器", "选购", "故障", "大盘鸡"] },
];

export function getPrimaryLabel(record: ContentRecord): string {
  const section = getRecordSection(record);
  const text = taxonomyText(record);
  const categoryText = normalizeSearch(record.categories.join(" "));

  if (section === "reviews") return "VPS 测评";
  if (section === "tools") {
    const category = record.categories.find((value) => TOOL_CATEGORIES.has(value));
    return category === "AI工具" ? "AI 工具" : category ?? "综合工具";
  }
  if (section === "resources") {
    const category = record.categories[0] ?? "";
    if (/云服务器/.test(category)) return "云服务目录";
    if (/运维服务/.test(category)) return "运维服务";
    if (/开发服务/.test(category)) return "开发服务";
    if (/存储与协作/.test(category)) return "存储与协作";
    if (/社交娱乐/.test(category)) return "社交与影音";
    if (/跨境支付/.test(category)) return "支付与出海";
    if (/网盘云盘|邮箱直链/.test(category)) return "存储与通信";
    if (/软件下载/.test(category)) return "软件资源";
    if (/域名服务商/.test(category)) return "域名服务";
    if (/科技lion|友情链接|社区与站点/.test(category)) return "社区与站点";
    if (/推广活动/.test(category)) return "活动与优惠";
    if (/esim/i.test(category)) return "数字通信";
    if (record.content_type === "navigation") return "内容导航";
    if (/隐私|关于|联系|条款|联盟|广告|投稿|友情链接|站点地图|标签/.test(record.title)) return "站务说明";
    return "综合资源";
  }

  if (/应用部署|建站/.test(categoryText)) return "部署与容器";
  if (/故障|排障|排查|恢复|回滚/.test(categoryText)) return "故障排查";
  if (/ai.*开发|开发环境/.test(categoryText)) return "AI 与开发";
  if (/linux|系统与安全/.test(categoryText)) return "Linux 与系统";
  if (/网络教程|网络工具|网络与代理/.test(categoryText)) return "网络与安全";
  if (/vps|大盘鸡|独立服务器|稳定建站|国内vps/.test(categoryText)) return "VPS 指南";

  return KNOWLEDGE_LABELS.find(({ terms }) => terms.some((term) => text.includes(normalizeSearch(term))))?.label ?? "综合技术";
}

export function getDisplayTags(record: ContentRecord, limit = 5): string[] {
  const primary = getPrimaryLabel(record);
  const values = [primary, ...record.categories, ...record.tags]
    .map((value) => value.trim())
    .filter((value) => value && !/未分类|科技lion/i.test(value));
  return [...new Set(values)].slice(0, limit);
}

export function getDisplayTitle(record: ContentRecord): string {
  if (record.content_type === "directory_entry") return record.title.split("：")[0]?.trim() || record.title;
  return record.title;
}

export function getDisplaySummary(record: ContentRecord): string {
  if (isVpsReview(record)) return record.summary;
  const section = getRecordSection(record);
  if (section === "tools") {
    const name = getDisplayTitle(record);
    const category = getPrimaryLabel(record);
    return `${name} 收录于${category}分类，便于按使用场景查找相关工具与服务。`;
  }
  if (section === "resources") {
    if (record.editorial_status === "published") return record.summary;
    return `本条内容归入${getPrimaryLabel(record)}，提供服务用途与场景索引，便于与技术教程、在线工具和测评内容分开查找。`;
  }
  if (record.editorial_status === "published") return record.summary;
  const topics = [...record.categories, ...record.tags]
    .filter((value) => !/未分类|友情链接|推广活动/.test(value))
    .slice(0, 4)
    .join("、");
  const focus = topics || getDisplayTitle(record);
  return `本条内容聚焦${focus}，整理相关概念、使用场景与关键检查项，便于快速检索和后续实践。`;
}

export function getDisplayFacts(record: ContentRecord): string[] {
  if (isVpsReview(record)) return record.key_facts;
  return record.key_facts.filter((fact) => !/原文|来源|源站|VPSKnow|KejiLion|科技\s*Lion|猫不腻/i.test(fact));
}

export const SECTION_LABELS: Record<ContentSection, string> = {
  knowledge: "知识库",
  tools: "在线工具",
  reviews: "VPS 测评",
  resources: "资源导航",
};

const TOPIC_TERMS: Record<ContentTopic, string[]> = {
  linux: ["linux", "脚本", "系统", "docker", "shell", "命令", "运维"],
  network: ["网络", "代理", "安全", "ip", "bgp", "线路", "带宽", "dns"],
  deploy: ["建站", "部署", "域名", "网站", "nginx", "wordpress", "面板", "docker", "compose"],
  troubleshoot: ["故障", "排障", "排查", "恢复", "回滚", "错误", "失败"],
  ai: ["ai", "chatgpt", "开发环境", "编程", "代码"],
  "vps-guide": ["选购", "入门", "故障", "排查", "使用指南"],
  regions: ["美国", "加拿大", "香港", "新加坡", "日本", "欧洲", "德国", "台湾", "韩国", "vps"],
};

export function matchesTopic(record: ContentRecord, topic: ContentTopic | "all" | undefined): boolean {
  if (!topic || topic === "all") return true;
  const haystack = taxonomyText(record);
  return TOPIC_TERMS[topic].some((term) => haystack.includes(normalizeSearch(term)));
}

export function matchesRecord(record: ContentRecord, filters: ContentFilters): boolean {
  if (filters.source && filters.source !== "all" && record.source !== filters.source) return false;
  if (filters.section && filters.section !== "all" && getRecordSection(record) !== filters.section) return false;
  if (!matchesTopic(record, filters.topic)) return false;
  if (filters.type && filters.type !== "all" && record.content_type !== filters.type) return false;
  if (filters.category && filters.category !== "all" && getPrimaryLabel(record) !== filters.category && !record.categories.includes(filters.category)) return false;
  if (filters.year && filters.year !== "all" && getRecordYear(record) !== filters.year) return false;

  const query = normalizeSearch(filters.query ?? "");
  if (!query) return true;

  const haystack = taxonomyText(record);

  return query.split(" ").every((term) => haystack.includes(term));
}

export function sortRecords(records: ContentRecord[], sort: ContentSort): ContentRecord[] {
  return [...records].sort((left, right) => {
    if (sort === "title") return left.title.localeCompare(right.title, "zh-CN");
    if (sort === "source") {
      return left.source.localeCompare(right.source) || left.title.localeCompare(right.title, "zh-CN");
    }

    const leftDate = left.published_at ?? "0000-00-00";
    const rightDate = right.published_at ?? "0000-00-00";
    return sort === "oldest"
      ? leftDate.localeCompare(rightDate)
      : rightDate.localeCompare(leftDate);
  });
}

export function countValues(values: string[][]): Array<{ label: string; count: number }> {
  const counts = new Map<string, number>();
  values.flat().forEach((value) => counts.set(value, (counts.get(value) ?? 0) + 1));
  return [...counts.entries()]
    .map(([label, count]) => ({ label, count }))
    .sort((left, right) => right.count - left.count || left.label.localeCompare(right.label, "zh-CN"));
}

export function formatDate(value: string | null): string {
  if (!value) return "日期待核";
  const [year, month, day] = value.slice(0, 10).split("-");
  return `${year}.${month}.${day}`;
}
